function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { PageHeader, Ribbon, Card, Empty, Search, PlunkDropDown } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { fetchSales, num, titleCase } from "../sales/territory-common";
import { ShowAll, ChipRow } from "../sales/territory-redesign-kit";
import { Menu } from "../products/px-common";
import { CategoryTrendChart, OosTrendChart, niceDate, trendRows } from "./charts";

/*
 * Inventory planner (redesign 2026-10-09, PlunkDocs mockups/sales-forecasts-inventory-redesign-2026-10-09.html,
 * board "7 · Inventory planner"). One list, "What to reorder", most urgent first, and the tiles above it
 * are counted from the SAME rows (GET /v1/forecast/restock: the product forecast where one exists, recent
 * sales otherwise). The old page had a velocity-only queue and a forecast "demand model" side by side; when
 * the velocity side read zero demand the two told opposite stories, so the page now has one source.
 *
 *   GET /v1/inventory/summary                     gate (404 = not connected) + categories + per-category out of stock
 *   GET /v1/forecast/restock?window=&limit=        the list + tiles (totals) for the "Warn me N weeks ahead" window
 *   GET /v1/inventory/categories/trend?category=&range=custom_…&grain=month   units sold vs stock, last 12 months
 *
 * Owner surface: unit counts only, never money. Plain words only (no ATP, runway, velocity).
 */

const ALL = "__all__";
const CAP = 15;
const TOP_CATS = 4;
const WEEK_OPTIONS = [1, 2, 3, 4, 6, 8, 12];
const LS_KEY = "plunk.inventory.warnWeeks";
const weeksLabel = w => `${w} week${w === 1 ? "" : "s"}`;
const catLabel = c => c === "(uncategorized)" ? "Uncategorized" : titleCase(c || "");
const fmtRate = v => {
  const n = Number(v) || 0;
  return n >= 100 ? num(Math.round(n)) : n.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
};
function readWeeks() {
  try {
    const v = Number(window.localStorage.getItem(LS_KEY));
    return WEEK_OPTIONS.includes(v) ? v : null;
  } catch (e) {
    return null;
  }
}
function saveWeeks(w) {
  try {
    window.localStorage.setItem(LS_KEY, String(w));
  } catch (e) {/* per-viewer convenience only */}
}

// Last 12 whole months through today, as the platform's custom range token.
function last12Months() {
  const now = new Date();
  const s = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const f = d => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `custom_${f(s)}_${f(now)}`;
}
function OrderBy({
  r
}) {
  const d = Number(r.daysUntilOrderBy);
  if (!r.orderBy || !isFinite(d)) return /*#__PURE__*/React.createElement("span", {
    className: "inv-dim"
  }, "Not needed this year");
  if (r.status === "past") {
    const late = Math.abs(d);
    const wk = Math.round(late / 7);
    const text = late < 7 ? `${late || 1} day${late === 1 ? "" : "s"} late` : `${wk} week${wk === 1 ? "" : "s"} late`;
    return /*#__PURE__*/React.createElement("span", {
      className: "inv-flag bad",
      title: `Order-by date was ${niceDate(r.orderBy)}`
    }, text);
  }
  if (r.status === "due") return /*#__PURE__*/React.createElement("span", {
    className: "inv-flag warn"
  }, niceDate(r.orderBy));
  return /*#__PURE__*/React.createElement("span", null, niceDate(r.orderBy));
}
function RunsOut({
  r
}) {
  if (Number(r.atp) <= 0) return /*#__PURE__*/React.createElement("span", {
    className: "inv-out"
  }, "Out now");
  if (!r.stockoutDate) return /*#__PURE__*/React.createElement("span", {
    className: "inv-dim"
  }, "Over a year");
  return /*#__PURE__*/React.createElement("span", null, niceDate(r.stockoutDate));
}
class InventoryPlannerComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "loadList", async () => {
      this.setState({
        listLoading: true,
        listFailed: false
      });
      try {
        const restock = await fetchSales(`/forecast/restock?window=${this.state.weeks * 7}&limit=2000`);
        if (this._alive) this.setState({
          restock,
          listLoading: false,
          listFailed: !restock
        });
      } catch (e) {
        if (this._alive) this.setState({
          restock: null,
          listLoading: false,
          listFailed: true
        });
      }
    });
    _defineProperty(this, "loadTrend", async () => {
      const cat = this.trendCategory();
      if (!cat || cat === this.state.trendCat) return;
      this.setState({
        trendCat: cat,
        trendLoading: true
      });
      const base = `/inventory/categories/trend?category=${encodeURIComponent(cat)}&range=${last12Months()}`;
      // Monthly for the units-sold chart; weekly for the out-of-stock line (stock history is short).
      const [trend, weekly] = await Promise.all([fetchSales(`${base}&grain=month`).catch(() => null), fetchSales(`${base}&grain=week`).catch(() => null)]);
      if (this._alive && this.state.trendCat === cat) this.setState({
        trend,
        oosWeekly: weekly,
        trendLoading: false
      });
    });
    _defineProperty(this, "setWeeks", w => {
      saveWeeks(w);
      this.setState({
        weeks: w
      }, this.loadList);
    });
    _defineProperty(this, "setCat", c => this.setState({
      cat: c,
      expanded: false
    }, this.loadTrend));
    _defineProperty(this, "downloadCsv", () => {
      const rows = this.rows();
      const esc = v => {
        const s = String(v == null ? "" : v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = ["Product,SKU,Category,Available,Sells a month,Runs out,Order by,Suggested units,Based on"];
      rows.forEach(r => lines.push([titleCase(r.title), r.sku, catLabel(r.category), Math.max(0, Number(r.atp) || 0), fmtRate(r.unitsPerMonthForecast), Number(r.atp) <= 0 ? "Out now" : r.stockoutDate || "Over a year", r.orderBy || "", r.suggestedQty || "", r.basis === "forecast" ? "forecast" : "recent sales"].map(esc).join(",")));
      const blob = new Blob([lines.join("\n") + "\n"], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "what-to-reorder.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
    _defineProperty(this, "onChip", v => {
      if (v === "__more__") {
        this.setState({
          moreCats: true
        });
        return;
      }
      if (v === "__less__") {
        this.setState({
          moreCats: false
        });
        return;
      }
      this.setCat(v);
    });
    this.state = {
      loading: true,
      error: false,
      notAvailable: false,
      summary: null,
      restock: null,
      listLoading: false,
      listFailed: false,
      weeks: readWeeks(),
      cat: ALL,
      moreCats: false,
      search: "",
      expanded: false,
      trend: null,
      oosWeekly: null,
      trendCat: null,
      trendLoading: false
    };
    this._alive = true;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    await this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load() {
    let summary;
    try {
      summary = await fetchSales("/inventory/summary");
    } catch (e) {
      if (this._alive) this.setState({
        loading: false,
        notAvailable: true
      });
      return;
    }
    if (!this._alive) return;
    if (!summary || !summary.totals) {
      this.setState({
        loading: false,
        notAvailable: true
      });
      return;
    }
    const weeks = this.state.weeks || Math.max(1, Math.round((Number(summary.reviewWindowDays) || 14) / 7));
    this.setState({
      loading: false,
      summary,
      weeks
    }, () => {
      this.loadList();
      this.loadTrend();
    });
  }
  // The trend follows the category chip; "All" shows the top-selling category.
  trendCategory() {
    if (this.state.cat !== ALL) return this.state.cat;
    const first = (this.state.summary && this.state.summary.categories || [])[0];
    return first ? first.category : null;
  }
  rows() {
    const items = this.state.restock && this.state.restock.items || [];
    const q = this.state.search.trim().toLowerCase();
    return items.filter(r => {
      if (this.state.cat !== ALL && r.category !== this.state.cat) return false;
      if (!q) return true;
      return String(r.title || "").toLowerCase().includes(q) || String(r.sku || "").toLowerCase().includes(q);
    });
  }
  // ── header ────────────────────────────────────────────────────────────────
  renderHeader() {
    const {
      weeks
    } = this.state;
    return /*#__PURE__*/React.createElement(PageHeader, {
      title: "Inventory planner",
      subtitle: "What to reorder and when, from your stock levels and how fast things sell",
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: "inv-warn"
      }, /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "220px",
        value: weeks,
        onChange: this.setWeeks,
        options: WEEK_OPTIONS.map(w => [w, `Warn me ${weeksLabel(w)} ahead`])
      })), /*#__PURE__*/React.createElement(Menu, {
        label: "More",
        triggerCls: "inv-more",
        items: [{
          key: "csv",
          label: "Download this list (CSV)",
          onClick: this.downloadCsv,
          disabled: !this.rows().length
        }, {
          key: "refresh",
          label: "Refresh",
          onClick: this.loadList
        }]
      }))
    });
  }

  // ── tiles: counted from the same rows as the list ─────────────────────────
  renderTiles() {
    const {
      restock,
      weeks,
      listLoading
    } = this.state;
    const t = restock && restock.totals;
    const v = x => t ? num(x) : "–";
    return /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: 4,
      loading: listLoading && !restock,
      tiles: [{
        l: "Order now",
        v: v(t && t.orderNow),
        s: "already past the date to reorder"
      }, {
        l: `Order in the next ${weeksLabel(weeks)}`,
        v: v(t && t.orderSoon),
        s: "items"
      }, {
        l: "Out of stock",
        v: v(t && t.outOfStock),
        s: "items that sell but have none available"
      }, {
        l: "Available to sell",
        v: v(t && Math.round(Number(t.available) || 0)),
        s: "units in stock or due in, not yet sold"
      }]
    });
  }

  // ── the one list ──────────────────────────────────────────────────────────
  catOptions() {
    const cats = (this.state.summary.categories || []).filter(c => c.category);
    const {
      cat,
      moreCats
    } = this.state;
    let shown = moreCats ? cats : cats.slice(0, TOP_CATS);
    // Keep a picked category visible even when the row is collapsed.
    if (cat !== ALL && !shown.some(c => c.category === cat)) shown = [...shown, cats.find(c => c.category === cat)].filter(Boolean);
    const opts = [{
      v: ALL,
      l: "All"
    }, ...shown.map(c => ({
      v: c.category,
      l: catLabel(c.category)
    }))];
    const hidden = cats.length - shown.length;
    if (!moreCats && hidden > 0) opts.push({
      v: "__more__",
      l: `+${hidden}`
    });
    if (moreCats && cats.length > TOP_CATS) opts.push({
      v: "__less__",
      l: "Fewer"
    });
    return opts;
  }
  renderList() {
    const {
      restock,
      listLoading,
      listFailed,
      expanded,
      search,
      cat
    } = this.state;
    const rows = this.rows();
    const shown = expanded ? rows : rows.slice(0, CAP);
    const anyItems = restock && (restock.items || []).length > 0;
    let empty = null;
    if (listFailed) empty = /*#__PURE__*/React.createElement(React.Fragment, null, "The list couldn't be loaded right now. ", /*#__PURE__*/React.createElement("a", {
      className: "tr-link",
      href: "#",
      onClick: e => {
        e.preventDefault();
        this.loadList();
      }
    }, "Try again"), ".");else if (!anyItems) empty = "Nothing to reorder. None of your stocked items has sales to plan around yet.";else if (!rows.length) empty = search ? "No products match that search." : `Nothing to reorder in ${catLabel(cat)}.`;
    return /*#__PURE__*/React.createElement(Card, {
      title: "What to reorder",
      hint: "most urgent first",
      cls: "inv-list-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tr-toolbar"
    }, /*#__PURE__*/React.createElement(ChipRow, {
      value: cat,
      onChange: this.onChip,
      options: this.catOptions()
    }), /*#__PURE__*/React.createElement(Search, {
      value: search,
      onChange: v => this.setState({
        search: v,
        expanded: false
      }),
      placeholder: "Product or SKU"
    })), listLoading && !restock ? /*#__PURE__*/React.createElement(Loader, null) : empty ? /*#__PURE__*/React.createElement(Empty, null, empty) : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table inv-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Available"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Sells a month"), /*#__PURE__*/React.createElement("th", null, "Runs out"), /*#__PURE__*/React.createElement("th", null, "Order by"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Suggested"), /*#__PURE__*/React.createElement("th", null, "Based on"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.sku
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, titleCase(r.title)), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, r.sku, " \xB7 ", catLabel(r.category))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(Math.max(0, Number(r.atp) || 0))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmtRate(r.unitsPerMonthForecast)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(RunsOut, {
      r: r
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(OrderBy, {
      r: r
    })), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, r.suggestedQty ? /*#__PURE__*/React.createElement("b", {
      className: "inv-suggest"
    }, num(r.suggestedQty), " units") : /*#__PURE__*/React.createElement("span", {
      className: "inv-dim"
    }, "\u2013")), /*#__PURE__*/React.createElement("td", {
      className: "inv-basis"
    }, r.basis === "forecast" ? "forecast" : "recent sales")))))), /*#__PURE__*/React.createElement(ShowAll, {
      total: rows.length,
      shown: CAP,
      open: expanded,
      onToggle: () => this.setState({
        expanded: !expanded
      }),
      allLabel: "Show all",
      fewerLabel: "Show fewer",
      note: "“Order by” is the run-out date minus your lead time. “Suggested” covers sales until a new order arrives, plus any back orders. Made-to-order items aren't listed."
    }));
  }

  // ── category trend + out of stock ─────────────────────────────────────────
  renderTrend() {
    const {
      summary,
      trend,
      trendCat,
      trendLoading
    } = this.state;
    if (!trendCat) return null;
    const catRow = (summary.categories || []).find(c => c.category === trendCat);
    const oos = catRow ? Number(catRow.outOfStock) || 0 : 0;
    const skus = catRow ? Number(catRow.skus) || 0 : 0;
    const name = catLabel(trendCat);
    const rows = trendRows(trend);
    const hasData = rows.some(r => r.sold > 0 || r.stock != null);
    const stockPts = rows.filter(r => r.stock != null);
    const since = trend && trend.seriesStart ? niceDate(trend.seriesStart) : summary.seriesStart ? niceDate(summary.seriesStart) : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "inv-trend"
    }, /*#__PURE__*/React.createElement(Card, {
      title: `Units sold vs stock, ${name}`,
      cls: "inv-trend-card"
    }, trendLoading ? /*#__PURE__*/React.createElement(Loader, null) : hasData ? /*#__PURE__*/React.createElement(CategoryTrendChart, {
      trend: trend
    }) : /*#__PURE__*/React.createElement(Empty, null, "No sales or stock recorded for ", name, " in the last 12 months."), /*#__PURE__*/React.createElement("p", {
      className: "inv-caption"
    }, "Bars are units on orders placed each month over the last 12 months (every order that was not cancelled, the same orders Sales counts).", " ", stockPts.length >= 2 ? `The line is stock available to sell at the end of each month${since ? `, starting ${since} when stock tracking began` : ""}.` : stockPts.length === 1 ? `Stock has one reading so far: ${num(stockPts[0].stock)} available in ${stockPts[0].label}. The stock line starts once there is a second month.` : "No stock readings yet for this category.")), /*#__PURE__*/React.createElement(Card, {
      title: `Out of stock in ${name}`,
      cls: "inv-oos-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "inv-oos-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "inv-oos-num" + (oos > 0 ? " bad" : "")
    }, num(oos)), /*#__PURE__*/React.createElement("span", {
      className: "inv-oos-of"
    }, "of ", num(skus), " items have none available")), this.state.oosWeekly ? /*#__PURE__*/React.createElement(OosTrendChart, {
      inventory: this.state.oosWeekly.inventory
    }) : null, /*#__PURE__*/React.createElement("p", {
      className: "inv-caption"
    }, oos > 0 ? "Each one that sells is lost orders. The list above shows what to reorder." : "Everything in this category has stock available.")));
  }
  renderNotConnected() {
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view inv-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Inventory",
      subtitle: "What's in stock, what's running low, and when to reorder"
    }), /*#__PURE__*/React.createElement("section", {
      className: "pm-connect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pm-connect-copy"
    }, /*#__PURE__*/React.createElement("h2", null, "Connect your stock levels to turn this on"), /*#__PURE__*/React.createElement("p", null, "Once Plunk knows how many of each product you have, this page shows what will run out first, when to reorder, and how stock moves by category. Your sales history is already here, so the reorder list works from the first day."), /*#__PURE__*/React.createElement("ol", {
      className: "pm-steps"
    }, /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", null, "1"), "Upload a spreadsheet of SKUs and quantities, or"), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", null, "2"), "Connect your online store, or"), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", null, "3"), "Have your system send stock levels nightly")), /*#__PURE__*/React.createElement("div", {
      className: "pm-actions"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pm-btn primary",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Connect stock levels"))), /*#__PURE__*/React.createElement("div", {
      className: "pm-preview",
      "aria-label": "Example of what this page shows"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-preview-l"
    }, "What you'll see"), /*#__PURE__*/React.createElement("div", {
      className: "pm-preview-row"
    }, /*#__PURE__*/React.createElement("span", null, "Order now"), /*#__PURE__*/React.createElement("b", null, "12 products")), /*#__PURE__*/React.createElement("div", {
      className: "pm-preview-row stack"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-preview-line"
    }, /*#__PURE__*/React.createElement("span", null, "Sofa"), /*#__PURE__*/React.createElement("span", {
      className: "bad"
    }, "runs out in about 2 weeks")), /*#__PURE__*/React.createElement("span", {
      className: "pm-bar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bad",
      style: {
        width: "18%"
      }
    }))), /*#__PURE__*/React.createElement("div", {
      className: "pm-preview-row stack"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-preview-line"
    }, /*#__PURE__*/React.createElement("span", null, "Swivel chair"), /*#__PURE__*/React.createElement("span", {
      className: "warn"
    }, "about 6 weeks left")), /*#__PURE__*/React.createElement("span", {
      className: "pm-bar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "warn",
      style: {
        width: "46%"
      }
    }))), /*#__PURE__*/React.createElement("span", {
      className: "pm-muted pm-small"
    }, "Example only"))));
  }
  render() {
    const {
      loading,
      error,
      notAvailable
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (loading) return /*#__PURE__*/React.createElement(Loader, null);
    if (notAvailable) return this.renderNotConnected();
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view tr-view wa-view inv-planner"
    }, this.renderHeader(), this.renderTiles(), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, this.renderList()), this.renderTrend());
  }
}
export default withRouter(InventoryPlannerComponent);